import React, { useState, useEffect, useMemo } from "react";
import { Search, Users, ArrowUpAZ, ArrowDownZA } from "@/components/icons/index"
import MaintenanceHeader from '@/components/maintenance/MaintenanceHeader'
import MaintenancePageLayout from '@/components/maintenance/MaintenancePageLayout'
import MaintenanceFilterPanel from "@/components/maintenance/MaintenanceFilterPanel";
import {
    maintenanceBtnIconCompact,
    maintenanceFieldInput,
    maintenanceFieldSelect,
    maintenanceLabel,
    maintenanceMutedText,
    maintenancePaginationWrap,
} from "@/components/maintenance/maintenanceTheme";
import useAuthUser from "@/hooks/useAuthUser";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CustomerModal from "@/components/customers/CustomerModal";
import MaintenanceDataGrid from "@/components/maintenance/MaintenanceDataGrid";
import Pagination from "./PortPlace/Pagination";
import { cn } from "@/lib/utils";
import { callLoader } from "@/redux/Loader/actions";
import { getCustomerList } from "@/redux/Masters/customers/actions";
import { getCustomerColumns, getCustomerExportColumns } from "@/components/customers/helper";
import { useDebounced } from "@/hooks/useDebounced";


const MaintainCustomer = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const authUser = useAuthUser();

    const { roleList } = useSelector((state) => state.common);
    const { customersList, customersPagination } = useSelector((state) => state.customers);

    const initialState = {
        PROFILE_EMAILID: "",
        CUSTCOMPANYLOGOLIGHT: null,
        PROFILE_FIRSTNAME: "",
        CUST_ID: 0,
        COMPANYNAME: "",
        CUSTCOMPANYLOGODARK: null,
        PROFILE_USERNAME: "",
        CREATED_BY: "",
        ISACTIVE: true,
        ROLEID: 0,
        PROFILE_PASSWORD: "",
        PROFILE_LASTNAME: "",
        CUSTCOMPANYLOGOMOBILE: null,
        COMPANY_ID: 0,
        ADB_COMPANY_ID: 0,
    };

    const [formData, setFormData] = useState(initialState);
    const [openModal, setOpenModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [searchText, setSearchText] = useState("");
    const [searchField, setSearchField] = useState("ALL");
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [sortDirection, setSortDirection] = useState("desc");
    const [sortBy, setSortBy] = useState("recent");
    const [visiblePasswords, setVisiblePasswords] = useState({});
    const [logoLight, setLogoLight] = useState("");
    const [logoDark, setLogoDark] = useState("");
    const [logoMobile, setLogoMobile] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    // console.log('selectedCustomer: ', selectedCustomer);


    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleAddNew = () => {
        setFormData(initialState);
        setErrors({});
        setOpenModal(true);
    };

    const handleEdit = (row) => {
        setFormData({
            CUST_ID: row?.CUST_ID?.toString() || "0",
            COMPANY_ID: row?.COMPANY_ID || authUser?.companyId || 0,
            ADB_COMPANY_ID: row?.ADB_COMPANY_ID || 0,

            PROFILE_FIRSTNAME: row?.PROFILE_FIRSTNAME || "",
            PROFILE_LASTNAME: row?.PROFILE_LASTNAME || "",
            PROFILE_EMAILID: row?.PROFILE_EMAILID || "",
            PROFILE_USERNAME: row?.PROFILE_USERNAME || "",
            PROFILE_PASSWORD: row?.PROFILE_PASSWORD || "",

            COMPANYNAME: row?.ADB_COMPANY_NAME || "",

            ROLEID: row?.ROLEID?.toString() || "",

            CUSTCOMPANYLOGOLIGHT: row?.COMPANYLOGOLIGHT || "",
            CUSTCOMPANYLOGODARK: row?.COMPANYLOGODARK || "",
            CUSTCOMPANYLOGOMOBILE: row?.COMPANYLOGOMOBILE || "",

            ISACTIVE: row?.ISACTIVE ?? true,

            CREATED_BY: row?.CREATED_BY || authUser?.username || "",

            MODIFIED_BY: row?.MODIFIED_BY || authUser?.username || "",
        });

        // Set the existing logo preview
        setLogoLight(row?.COMPANYLOGOLIGHT || "");
        setLogoDark(row?.COMPANYLOGODARK || "");
        setLogoMobile(row?.COMPANYLOGOMOBILE || "");

        setErrors({});
        setSelectedCustomer(row);
        setOpenModal(true);
    };

    const handleSaveCustomer = (isEdit = false) => {
        _getCustomerList(isEdit ? currentPage : 1);
    };

    const handlePrevious = () => {
        if (currentPage > 1) _getCustomerList(currentPage - 1);
    };
    const handleNext = () => {
        if (customersPagination?.nextPage > 0) _getCustomerList(customersPagination.nextPage);
    };
    const handleGoto = (page) => {
        _getCustomerList(page);
    };

    const togglePasswordVisibility = (rowKey) =>
        setVisiblePasswords((prev) => ({
            ...prev,
            [rowKey]: !prev[rowKey],
        }));

    const filteredList = useMemo(() => {
        const s = (searchText || "").toLowerCase().trim();

        if (!s) return customersList || [];

        return (customersList || []).filter((item) => {
            const firstName = item?.PROFILE_FIRSTNAME?.toLowerCase() || "";
            const lastName = item?.PROFILE_LASTNAME?.toLowerCase() || "";
            const email = item?.PROFILE_EMAILID?.toLowerCase() || "";
            const username = item?.PROFILE_USERNAME?.toLowerCase() || "";
            const role = item?.ROLE_NAME?.toLowerCase() || "";

            if (searchField === "NAME") {
                return firstName.includes(s) || lastName.includes(s);
            }

            if (searchField === "EMAIL") {
                return email.includes(s);
            }

            if (searchField === "ROLE") {
                return role.includes(s);
            }

            if (searchField === "USERNAME") {
                return username.includes(s);
            }

            return (
                firstName.includes(s) ||
                lastName.includes(s) ||
                username.includes(s) ||
                email.includes(s) ||
                role.includes(s)
            );
        });
    }, [customersList, searchText, searchField]);

    const sortedList = useMemo(() => {
        if (sortBy === "recent") return filteredList;

        const list = [...filteredList];

        const cmp = (a, b) => {
            const fa =
                sortBy === "name"
                    ? `${a?.PROFILE_FIRSTNAME || ""} ${a?.PROFILE_LASTNAME || ""
                        }`
                        .trim()
                        .toLowerCase()
                    : (a?.PROFILE_USERNAME || "").toLowerCase();

            const fb =
                sortBy === "name"
                    ? `${b?.PROFILE_FIRSTNAME || ""} ${b?.PROFILE_LASTNAME || ""
                        }`
                        .trim()
                        .toLowerCase()
                    : (b?.PROFILE_USERNAME || "").toLowerCase();

            return fa.localeCompare(fb);
        };

        list.sort(cmp);

        return sortDirection === "asc" ? list : list.reverse();
    }, [filteredList, sortBy, sortDirection]);

    // Fetches a page from the server and syncs currentPage to what was
    // actually requested (previously hardcoded back to 1 every call).
    const _getCustomerList = async (page = 1) => {
        setCurrentPage(page);
        dispatch(callLoader(true));
        try {
            await dispatch(getCustomerList({
                iPageNo: page,
                iPageRecords: recordsPerPage,
                SearchKeyword: searchText,
                CompanyId: authUser?.companyId,
            }));
        } catch (error) {
            console.error("Error fetching customer list:", error);
        } finally {
            dispatch(callLoader(false));
        }
    }

    // Debounced so typing doesn't fire an API call per keystroke.
    const debouncedSearch = useDebounced(() => _getCustomerList(1), 400);

    // Initial load + whenever page size changes, refetch page 1 immediately.
    useEffect(() => {
        _getCustomerList(1);
    }, [recordsPerPage]);

    // Search is server-filtered too (SearchKeyword), so debounce the refetch.
    // sortBy/sortDirection are intentionally NOT deps here — sorting is fully
    // client-side via `sortedList`, so changing it never needs a server round-trip.
    useEffect(() => {
        debouncedSearch();
    }, [searchText]);

    const columns = useMemo(
        () =>
            getCustomerColumns(
                { handleEdit },
                {
                    visiblePasswords,
                    togglePasswordVisibility,
                    getIndex: (i) => (currentPage - 1) * recordsPerPage + i + 1,
                    t,
                }
            ),
        [visiblePasswords, currentPage, recordsPerPage, t]
    );

    return (
        <MaintenancePageLayout>
            <MaintenanceHeader
                icon={Users}
                title={t('customersPage.maintainCustomers')}
                subtitle={t('customersPage.subtitle')}
                accent="blue"
                addLabel={t('customersPage.createNewCustomer')}
                addTestId="add-customer-btn"
                onAdd={handleAddNew}
                onRefresh={() => _getCustomerList(currentPage)}
                exportConfig={{
                    fileName: "Customers",
                    sheetName: "Customers",
                    columns: getCustomerExportColumns(t),
                    getRows: () => sortedList,
                }}
            />

            <MaintenanceFilterPanel
                footer={
                    <div className="flex items-center justify-between">
                        <p className={`text-[12.5px] ${maintenanceMutedText}`}>
                            {t('maintenance.showing')}{" "}
                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                {customersPagination?.currentRecord || sortedList.length}
                            </span>{" "}
                            {t('maintenance.of')}{" "}
                            <span className="font-medium text-gray-700 dark:text-gray-300">
                                {customersPagination?.totalRecord || sortedList.length}
                            </span>{" "}
                            {t('customersPage.customers').toLowerCase()}
                        </p>
                        {searchText ? (
                            <button
                                type="button"
                                onClick={() => setSearchText("")}
                                className="text-[12px] text-blue-600 dark:text-blue-400 hover:underline font-medium"
                            >
                                {t('maintenance.clearFilters')}
                            </button>
                        ) : null}
                    </div>
                }
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3 items-end">
                    <div className="sm:col-span-1 lg:col-span-3 flex flex-col gap-1 min-w-0">
                        <label htmlFor="users-search-field-select" className={maintenanceLabel}>
                            {t('maintenance.searchIn')}
                        </label>
                        <select
                            id="users-search-field-select"
                            value={searchField}
                            onChange={(e) => setSearchField(e.target.value)}
                            className={maintenanceFieldSelect}
                        >
                            <option value="ALL">{t('maintenance.allFields')}</option>
                            <option value="NAME">{t('customersPage.name')}</option>
                            <option value="EMAIL">{t('customersPage.email')}</option>
                            <option value="ROLE">{t('customersPage.role')}</option>
                            <option value="USERNAME">{t('customersPage.username')}</option>
                        </select>
                    </div>

                    <div className="sm:col-span-1 lg:col-span-5 flex flex-col gap-1 min-w-0">
                        <label htmlFor="users-search-field" className={maintenanceLabel}>
                            {t('maintenance.keyword')}
                        </label>
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                id="users-search-field"
                                type="text"
                                placeholder={t('customersPage.searchPlaceholder')}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                autoComplete="off"
                                name="users-search-field"
                                className={cn(maintenanceFieldInput, "pl-9")}
                            />
                        </div>
                    </div>

                    <div className="sm:col-span-2 lg:col-span-4 flex flex-col gap-1 min-w-0">
                        <span className={maintenanceLabel}>{t('maintenance.sortBy')}</span>
                        <div className="flex items-center gap-2 min-w-0">
                            <button
                                type="button"
                                onClick={() =>
                                    setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
                                }
                                className={cn(maintenanceBtnIconCompact, "h-9 w-9 shrink-0")}
                                title={`${t('maintenance.sortBy')} ${sortDirection === "asc" ? t('maintenance.sortDescending') : t('maintenance.sortAscending')}`}
                                aria-label={`${t('maintenance.sortBy')} ${sortDirection === "asc" ? t('maintenance.sortDescending') : t('maintenance.sortAscending')}`}
                            >
                                {sortDirection === "asc" ? (
                                    <ArrowUpAZ className="w-4 h-4" />
                                ) : (
                                    <ArrowDownZA className="w-4 h-4" />
                                )}
                            </button>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className={cn(maintenanceFieldSelect, "flex-1 min-w-0")}
                                aria-label={t('maintenance.sortBy')}
                            >
                                <option value="recent">{t('maintenance.mostRecent')}</option>
                                <option value="name">{t('maintenance.fullName')}</option>
                                <option value="username">{t('customersPage.username')}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </MaintenanceFilterPanel>

            <MaintenanceDataGrid
                title={t('Cusomter List')}
                recordCount={sortedList?.length || 0}
                data={sortedList || []}
                columns={columns}
                Icon={Users}
                emptyText={t('No Customers Found')}
                isApplyingFilters={!!searchText}
                resetFilters={() => {
                    setSearchText("");
                    setSearchField("ALL");
                }}
            />

            {customersPagination?.totalRecord > 0 && (
                <Pagination
                    currentPage={currentPage}
                    nextPage={customersPagination?.nextPage}
                    recordsPerPage={recordsPerPage}
                    setRecordsPerPage={setRecordsPerPage}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    totalRecord={customersPagination?.totalRecord}
                    handleGoto={handleGoto}
                    title={t('customersPage.customers').toLowerCase()}
                    className={maintenancePaginationWrap}
                />
            )}

            <CustomerModal
                open={openModal}
                onClose={() => {
                    setOpenModal(false);
                    setFormData(initialState);
                    setErrors({});
                }}
                formData={formData}
                errors={errors}
                handleInputChange={handleInputChange}
                onSave={() => handleSaveCustomer(Number(formData?.CUST_ID) > 0)}
                editData={selectedCustomer}
                roleList={roleList}
            />
        </MaintenancePageLayout>
    )
}

export default MaintainCustomer